import { useRef } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { NavLink, Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import {
  Breadcrumb,
  DropdownMenu,
  FormControl,
  LoadingButton,
  MoreButton,
  PageTitle,
  formatId,
} from '@ingrid/luterkort-react';

import { MutationBanner } from '~src/components/MutationNotification/MutationNotification';
import { MutationSaveBar } from '~src/components/MutationSaveBar/MutationSaveBar';
import { useTranslation } from '~src/i18n/use-translation';
import { useToast } from '~src/main/hooks/useToast';
import { TrackingEventEnum, useTracking } from '~src/main/hooks/useTracking';

import { useShipmentUpdateMutation } from '../mutations';
import { ShipmentUpsert, useShipmentFormContext } from '../types';

export function Header() {
  const updateMutation = useShipmentUpdateMutation();

  const viewportRef = useRef(null);
  const navigate = useNavigate();
  const tracking = useTracking();

  const { handleSubmit } = useShipmentFormContext();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { id } = useParams();

  const onSubmit: SubmitHandler<ShipmentUpsert> = async shipment => {
    try {
      await updateMutation.mutateAsync({ ...shipment, shipment_id: id });

      toast({
        title: t('shipment.update.success'),
        variant: 'success',
      });

      tracking.capture(TrackingEventEnum.ShipmentEditCompleted);
      navigate(`../${id}`);
    } catch (e) {
      tracking.capture(TrackingEventEnum.ShipmentEditFailed);
      throw e;
    }
  };

  return (
    <>
      <FormControl.Form onSubmit={handleSubmit(onSubmit)} style={{ display: 'none' }} />

      <PageTitle.Wrapper ref={viewportRef}>
        <PageTitle.Stack>
          <Breadcrumb aria-label={t('common.breadcrumbs')}>
            <NavLink to="..">{t('common.shipments')}</NavLink>
            <span aria-current="page">{formatId(id)}</span>
          </Breadcrumb>
        </PageTitle.Stack>

        <FormControl.SubmitButton asChild>
          <LoadingButton loading={updateMutation.isPending}>{t('common.save')}</LoadingButton>
        </FormControl.SubmitButton>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger>
            <MoreButton variant="secondary" />
          </DropdownMenu.Trigger>

          <DropdownMenu.Content>
            <DropdownMenu.Item asChild>
              <RouterLink to={`../${id}`} onClick={() => tracking.capture(TrackingEventEnum.ShipmentEditCancel)}>
                {t('common.cancel')}
              </RouterLink>
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </PageTitle.Wrapper>

      <MutationBanner mutation={updateMutation} />

      <MutationSaveBar viewportRef={viewportRef}>
        <MutationSaveBar.Cancel
          to={`../${id}`}
          onClick={() => tracking.capture(TrackingEventEnum.ShipmentEditCancel)}
        />

        <MutationSaveBar.Submit
          isLoading={updateMutation.isPending}
          onClick={handleSubmit(onSubmit)}
          label={t('common.save')}
        />
      </MutationSaveBar>
    </>
  );
}
